/** One rep's performance in detail — reached from the Team Performance leaderboard. */
import { useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PageHeader, Card, StatTile, Meter, Badge, Spinner, ErrorState, Tabs } from '../../components/common/index.jsx';
import { BarChart } from '../../components/charts/index.jsx';
import { money, moneyShort, date, dateTime, number } from '../../utils/formatters.js';
import * as api from '../../api/teamPerformance.api.js';
import { StatusBadge, readPeriod, changePct, changePts } from './performanceShared.jsx';

const QUOTE_TONE = { accepted: 'emerald', declined: 'rose', rejected: 'rose', expired: 'neutral', sent: 'sky', viewed: 'sky', draft: 'neutral', pending_approval: 'amber', approved: 'sky' };

function Table({ head, rows, empty, children }) {
  if (!rows.length) return <div className="p-3 t-sm text-muted">{empty}</div>;
  return (<div className="table-x-wrap"><table className="table-x is-dense"><thead><tr>{head.map((h) => <th key={h}>{h}</th>)}</tr></thead><tbody>{children}</tbody></table></div>);
}

export default function RepPerformancePage() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const { period, from, to } = readPeriod(sp);
  const params = { period, from: period === 'custom' ? from : '', to: period === 'custom' ? to : '' };
  const [tab, setTab] = useState('opportunities');
  const q = useQuery({ queryKey: ['team-performance', 'rep', id, params], queryFn: () => api.getRepPerformance(id, params) });

  if (q.isLoading) return <Spinner />;
  if (q.isError) return <ErrorState error={q.error} onRetry={q.refetch} />;
  const d = q.data;
  const s = d.summary;
  const back = `/team-performance${sp.toString() ? `?${sp.toString()}` : ''}`;

  return (
    <>
      <PageHeader title={d.rep.name} subtitle={`${d.rep.territory ? `${d.rep.territory} · ` : ''}${d.period.label} · ${d.period.from} to ${d.period.to}`}
        breadcrumb={<Link to={back}><i className="bi bi-arrow-left me-1" aria-hidden="true" />Team performance</Link>}>
        <StatusBadge status={s.status} />
      </PageHeader>

      <div className="row g-3 mb-3">
        <div className="col-6 col-xl"><StatTile label="Achieved" value={moneyShort(s.achieved)} icon="bi-trophy-fill" tone="emerald" delta={changePct(s.achieved, s.prev.achieved)} meta={s.target ? `${s.attainmentPct}% of ${moneyShort(s.target)}` : 'No target set'} /></div>
        <div className="col-6 col-xl"><StatTile label="Pipeline" value={moneyShort(s.pipeline)} icon="bi-funnel-fill" tone="brand" meta={`${s.openDeals} open · forecast ${moneyShort(s.weighted)}`} /></div>
        <div className="col-6 col-xl"><StatTile label="Win rate" value={s.winRatePct} suffix="%" icon="bi-bullseye" tone="sky" delta={changePts(s.winRatePct, s.prev.winRatePct)} deltaSuffix=" pts" meta={`${s.won} won · ${s.lost} lost · ${s.avgCycleDays}d cycle`} /></div>
        <div className="col-6 col-xl"><StatTile label="Leads" value={number(s.leads)} icon="bi-magnet-fill" tone="violet" delta={changePct(s.leads, s.prev.leads)} meta={`${s.qualified} qualified · ${s.converted} converted`} /></div>
        <div className="col-6 col-xl"><StatTile label="Overdue follow-ups" value={s.overdue} icon="bi-exclamation-triangle-fill" tone="rose" meta={s.lastActivityAt ? `Last activity ${dateTime(s.lastActivityAt)}` : 'No activity yet'} /></div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-lg-7">
          <Card title="Last 6 months" subtitle="Revenue won against target">
            <BarChart data={d.trend} xKey="label" money height={230} series={[{ key: 'achieved', label: 'Achieved' }, { key: 'target', label: 'Target', color: '#cbd5e1' }]} />
          </Card>
        </div>
        <div className="col-lg-5">
          <Card title="Activity" subtitle="Completed per week in the period">
            {d.activities.weekly.length === 0 ? <div className="t-sm text-muted">No completed activities in this period.</div> : (
              <BarChart data={d.activities.weekly.map((w) => ({ ...w, week: date(w.week) }))} xKey="week" stacked height={230}
                series={[{ key: 'calls', label: 'Calls' }, { key: 'visits', label: 'Visits' }, { key: 'meetings', label: 'Meetings' }, { key: 'emails', label: 'Emails' }]} />
            )}
            <div className="t-xs text-muted mt-2">{s.calls} calls · {s.visits} visits · {s.meetings} meetings · {s.emails} emails</div>
          </Card>
        </div>
      </div>

      <Card bare>
        <div className="px-3 pt-2">
          <Tabs value={tab} onChange={setTab} tabs={[
            { key: 'opportunities', label: 'Opportunities', badge: d.opportunities.open.length },
            { key: 'leads', label: 'Leads', badge: d.leads.items.length },
            { key: 'quotations', label: 'Quotations', badge: d.quotations.items.length },
            { key: 'followups', label: 'Overdue follow-ups', badge: d.followups.overdue.length },
          ]} />
        </div>

        {tab === 'opportunities' && (
          <>
            <Table head={['Deal', 'Stage', 'Amount', 'Probability', 'Closes', 'In stage']} rows={d.opportunities.open} empty="No open opportunities.">
              {d.opportunities.open.map((o) => (
                <tr key={o.id}><td><div className="fw-600">{o.name}</div><div className="t-xs text-muted">{o.account ?? '—'} · {o.opportunity_no}</div></td>
                  <td>{o.stage}</td><td>{money(o.amount)}</td><td>{o.probability != null ? `${o.probability}%` : '—'}</td><td>{date(o.close_date)}</td>
                  <td>{o.days_in_stage >= 21 ? <Badge tone="rose">{o.days_in_stage}d</Badge> : `${o.days_in_stage}d`}</td></tr>
              ))}
            </Table>
            {d.opportunities.closed.length > 0 && (
              <div className="px-3 pb-3">
                <div className="eyebrow my-2">Closed in this period</div>
                {d.opportunities.closed.map((o) => (
                  <div key={o.id} className="d-flex justify-content-between t-sm py-1 border-top">
                    <span>{o.name} <span className="text-muted">· {o.account ?? '—'}</span></span>
                    <span><Badge tone={o.is_won ? 'emerald' : 'rose'}>{o.is_won ? 'Won' : 'Lost'}</Badge> {money(o.amount)} · {date(o.closed_on)}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === 'leads' && (
          <>
            <div className="px-3 pt-3 t-sm text-muted">
              {s.leads} created · {s.contacted} contacted · {s.qualified} qualified · {s.converted} converted ({s.conversionPct}%)
              {d.leads.avgResponseMinutes ? ` · average first response ${d.leads.avgResponseMinutes < 120 ? `${d.leads.avgResponseMinutes} min` : `${Math.round(d.leads.avgResponseMinutes / 60)} h`}` : ''}
            </div>
            <Table head={['Lead', 'Company', 'Status', 'Source', 'Score', 'Created']} rows={d.leads.items} empty="No leads created in this period.">
              {d.leads.items.map((l) => (
                <tr key={l.id}><td className="fw-600"><Link to={`/leads/${l.id}`}>{l.name}</Link></td><td>{l.company}</td>
                  <td>{l.status}{l.converted && <Badge tone="emerald" className="ms-1">Converted</Badge>}</td><td>{l.source ?? '—'}</td><td>{l.score ?? '—'}</td><td>{date(l.created_at)}</td></tr>
              ))}
            </Table>
          </>
        )}

        {tab === 'quotations' && (
          <>
            <div className="px-3 pt-3 t-sm text-muted">{s.quotesCreated} created · {s.quotesSent} sent · {s.quotesAccepted} accepted ({money(s.quotesAcceptedValue)}) · {s.quotesLost} declined or expired</div>
            <Table head={['Quotation', 'Account', 'Status', 'Total', 'Date']} rows={d.quotations.items} empty="No quotations in this period.">
              {d.quotations.items.map((x) => (
                <tr key={x.id}><td><div className="fw-600">{x.quote_no}</div><div className="t-xs text-muted">{x.title ?? ''}</div></td><td>{x.account ?? '—'}</td>
                  <td><Badge tone={QUOTE_TONE[x.status] ?? 'neutral'}>{x.status.replace('_', ' ')}</Badge></td><td>{money(x.total)}</td><td>{date(x.quote_date)}</td></tr>
              ))}
            </Table>
          </>
        )}

        {tab === 'followups' && (
          <Table head={['Activity', 'Type', 'Account', 'Was due']} rows={d.followups.overdue} empty="Nothing overdue — all follow-ups are up to date.">
            {d.followups.overdue.map((f) => (<tr key={f.id}><td className="fw-600">{f.subject}</td><td>{f.kind}</td><td>{f.account ?? '—'}</td><td><Badge tone="rose">{dateTime(f.due)}</Badge></td></tr>))}
          </Table>
        )}
      </Card>
    </>
  );
}
